import {
  MAX_AUDIO_DURATION_MS,
  MAX_MEDIA_BYTES,
  MIN_VOICE_DURATION_MS,
  sniffMime,
} from './zionMediaProtocol.ts'

export const CODE_TOO_SHORT = 'voice_too_short'
export const CODE_TOO_LONG = 'voice_too_long'
export const CODE_RECORD_FAILED = 'media_sanitize_failed'
export const CODE_TOO_LARGE = 'media_too_large'

export interface VoiceRecording {
  bytes: Uint8Array
  mime: string
  durationMs: number
}

const MIME_CANDIDATES = [
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
]

export function pickAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined') return ''
  for (const mime of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime
  }
  return ''
}

export class VoiceRecorderSession {
  private mediaRecorder: MediaRecorder | null = null
  private stream: MediaStream | null = null
  private chunks: Blob[] = []
  private startedAt = 0

  get isRecording(): boolean {
    return this.mediaRecorder != null && this.mediaRecorder.state !== 'inactive'
  }

  async start(): Promise<boolean> {
    if (this.isRecording) return false
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return false
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = pickAudioMimeType()
      this.mediaRecorder = mimeType
        ? new MediaRecorder(this.stream, { mimeType })
        : new MediaRecorder(this.stream)
      this.chunks = []
      this.mediaRecorder.ondataavailable = (ev) => {
        if (ev.data.size > 0) this.chunks.push(ev.data)
      }
      this.mediaRecorder.start(200)
      this.startedAt = Date.now()
      return true
    } catch {
      this.cleanup()
      return false
    }
  }

  elapsedMs(): number {
    return this.startedAt > 0 ? Math.max(0, Date.now() - this.startedAt) : 0
  }

  cancel(): void {
    const mr = this.mediaRecorder
    if (mr && mr.state !== 'inactive') {
      try {
        mr.stop()
      } catch {
        /* ignore */
      }
    }
    this.cleanup()
  }

  stop(): Promise<VoiceRecording | null> {
    const mr = this.mediaRecorder
    if (!mr || mr.state === 'inactive') return Promise.resolve(null)
    const durationMs = this.elapsedMs()

    return new Promise((resolve) => {
      mr.onstop = () => {
        void (async () => {
          const mime = mr.mimeType || pickAudioMimeType() || 'audio/webm'
          const blob = new Blob(this.chunks, { type: mime })
          this.cleanup()
          if (durationMs < 400 || blob.size === 0) {
            resolve(null)
            return
          }
          const bytes = new Uint8Array(await blob.arrayBuffer())
          resolve({ bytes, mime, durationMs })
        })()
      }
      try {
        mr.requestData()
        mr.stop()
      } catch {
        this.cleanup()
        resolve(null)
      }
    })
  }

  private cleanup(): void {
    this.mediaRecorder = null
    this.chunks = []
    this.startedAt = 0
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop()
      this.stream = null
    }
  }
}

export function validateVoiceRecording(rec: VoiceRecording): string | null {
  if (rec.durationMs < MIN_VOICE_DURATION_MS) return CODE_TOO_SHORT
  if (rec.durationMs > MAX_AUDIO_DURATION_MS) return CODE_TOO_LONG
  if (rec.bytes.length > MAX_MEDIA_BYTES) return CODE_TOO_LARGE
  const sniffed = sniffMime(rec.bytes)
  const mime = rec.mime.startsWith('audio/') ? rec.mime : sniffed
  if (!mime?.startsWith('audio/')) return CODE_RECORD_FAILED
  return null
}

export function formatVoiceElapsed(ms: number): string {
  const sec = Math.max(0, Math.floor(ms / 1000))
  const min = Math.floor(sec / 60)
  const rem = sec % 60
  return `${min}:${String(rem).padStart(2, '0')}`
}

export async function readAudioDurationMs(bytes: Uint8Array, mime: string): Promise<number> {
  const blob = new Blob([new Uint8Array(bytes)], { type: mime || 'audio/webm' })
  const url = URL.createObjectURL(blob)
  try {
    const audio = new Audio(url)
    await new Promise<void>((resolve, reject) => {
      audio.addEventListener('loadedmetadata', () => resolve(), { once: true })
      audio.addEventListener('error', () => reject(new Error('audio meta')), { once: true })
    })
    const d = audio.duration
    return Number.isFinite(d) && d > 0 ? Math.round(d * 1000) : 0
  } catch {
    return 0
  } finally {
    URL.revokeObjectURL(url)
  }
}
